import { LoopMedia } from "@/components/site/loop-media";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { BandLink, SealButton } from "@/components/site/ctas";
import { Link } from "@/components/site/link";
import { Reveal } from "@/components/site/motion";
import { getDict, href, type Lang } from "@/content/site";

export const Route = createFileRoute("/$lang/divisions/$slug")({
  loader: ({ params }) => {
    const lang = params.lang as Lang;
    const division = getDict(lang).divisions.find((d) => d.slug === params.slug);
    if (!division) throw notFound();
    return { lang, division };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [
        { title: `${loaderData.division.name} · insidermark` },
        { name: "description", content: loaderData.division.scope },
        { property: "og:title", content: `${loaderData.division.name} · insidermark` },
        { property: "og:image", content: loaderData.division.image },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { lang, division: d } = Route.useLoaderData();
  const t = getDict(lang);
  const p = t.divisionPage;
  const relatedMarkets = t.markets.filter((m) => m.divisions.includes(d.slug) && m.phase === 1);
  return (
    <main>
      <header className="im-page-head">
        <div className="im-wrap im-page-head__grid">
          <Reveal>
            <Link className="im-mono" to={href(lang, "/divisions")}>
              ← {p.back}
            </Link>
            <div className="mt-6 flex items-center gap-4">
              <img fetchPriority="high" src={d.icon} alt="" width={52} height={52} />
              <span className="im-mono">DIV {d.code}</span>
            </div>
            <h1 className="im-display mt-4 max-w-[14ch]">{d.name}</h1>
            <p className="im-body-text mt-5">{d.scope}</p>
          </Reveal>
          <Reveal axis="x">
            <LoopMedia className="im-hero-img" image={d.image} alt={d.name} />
          </Reveal>
        </div>
      </header>

      {d.capacity.length > 1 ? (
        <section className="im-section im-section--tight">
          <div className="im-wrap">
            <h2 className="im-h3 mb-6">{p.capacity}</h2>
            <dl className="im-facts">
              {d.capacity.map((c, i) => (
                <div key={`${c.label}-${i}`}>
                  <dt>{c.label}</dt>
                  <dd>{c.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      ) : null}

      <section className="im-section">
        <div className="im-wrap im-two">
          <Reveal>
            <h2 className="im-display-sm">{p.products}</h2>
          </Reveal>
          <ul className="im-list">
            {d.products.map((pr) => (
              <li key={pr.name}>
                <strong>{pr.name}</strong>
                <span>{pr.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="im-section im-section--tight">
        <div className="im-plate im-plate--manifest" aria-hidden="true" />
        <div className="im-wrap im-rel grid gap-10 md:grid-cols-3">
          <div>
            <h3 className="im-mono mb-3">{p.buyer}</h3>
            <p className="im-acc__text">{d.buyer}</p>
          </div>
          <div>
            <h3 className="im-mono mb-3">{p.markets}</h3>
            <p className="im-acc__text">{d.markets}</p>
            {relatedMarkets.length ? (
              <div className="mt-4">
                {relatedMarkets.map((m) => (
                  <BandLink key={m.slug} to={href(lang, `/markets/${m.slug}`)} meta={m.region}>
                    {m.name}
                  </BandLink>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <h3 className="im-mono mb-3">{p.constraint}</h3>
            <p className="im-acc__text">{d.constraint}</p>
          </div>
        </div>
      </section>

      <section className="im-section">
        <div className="im-wrap im-closing">
          <h2 className="im-display-sm max-w-[18ch]">{p.rfq}</h2>
          <p className="im-mono">{p.catalogNote}</p>
          <div className="im-closing__ctas">
            <SealButton to={href(lang, `/rfq/${d.rfq}?division=${d.slug}`)}>{t.nav.rfq}</SealButton>
          </div>
        </div>
      </section>
    </main>
  );
}
